import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, CheckCircle, XCircle, BarChart3, Lock, Users, Globe } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import SEOMetadata from '@/components/SEOMetadata';

const EthicalProxyArticle = () => {
  return (
    <>
      <SEOMetadata 
        title="Ethical Proxy Use in the Fight Against Internet Fraud - NEXX News"
        description="A case study on how RetailShield Ltd. and Nexx Telecom used ethical proxy solutions to combat coordinated fraud networks and prevent $120,000 in chargebacks."
        keywords="ethical proxies, internet fraud prevention, cybersecurity, proxy compliance, fraud detection, retail security"
        canonical="https://news.nexx.net/ethical-proxy-fraud-prevention"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Ethical Proxy Use in the Fight Against Internet Fraud",
          "description": "A comprehensive case study on how ethical proxy solutions can combat internet fraud",
          "author": {
            "@type": "Organization",
            "name": "NEXX Telecom"
          },
          "publisher": {
            "@type": "Organization",
            "name": "NEXX News",
            "logo": {
              "@type": "ImageObject",
              "url": "https://news.nexx.net/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png"
            }
          },
          "datePublished": "2024-09-11"
        }}
      />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <Link to="/news" className="flex items-center space-x-3">
                  <img 
                    src="/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png" 
                    alt="NEXX"
                    className="h-8 w-auto"
                  />
                  <span className="font-semibold text-lg text-foreground">News</span>
                </Link>
              </div>
              <div className="flex items-center space-x-3">
                <Link to="https://nexx.net">
                  <Button variant="outline" size="sm">
                    Main Site
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <div className="border-b bg-muted/30">
          <div className="container mx-auto px-4 py-3">
            <Link to="/news" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to News
            </Link>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="container mx-auto px-4 py-16 relative">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <Badge variant="secondary" className="px-4 py-2 text-sm">
                  Case Study
                </Badge>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground leading-tight">
                Ethical Proxy Use in the Fight Against Internet Fraud
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                A Case Study on RetailShield Ltd. and Nexx Telecom
              </p>
              <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  NEXX Telecom Research
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  September 11, 2024
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  8 min read
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="container mx-auto px-4 py-16 max-w-4xl">
          
          {/* Introduction */}
          <section className="mb-16">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <Shield className="h-8 w-8 text-primary" />
                The Dual Nature of Proxies
              </h2>
              <p className="text-lg leading-relaxed mb-6">
                Proxies have always been tools with two faces. For cybercriminals, they are cloaks used to mask identities and commit fraud. For cybersecurity professionals, they are shields—tools for monitoring, simulating, and dismantling malicious activity.
              </p>
              <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg mb-8">
                <p className="text-lg font-medium">
                  At Nexx Telecom, we believe proxies should be reclaimed for ethical use. Our mission is to ensure proxies serve as instruments of protection and accountability, not exploitation.
                </p>
              </div>
            </div>
          </section>

          <Separator className="my-12" />

          {/* The Case */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <Globe className="h-8 w-8 text-destructive" />
              The Case: RetailShield Ltd. vs. Coordinated Fraud Network
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <Card className="border-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-primary">Company Background</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    RetailShield Ltd., a Caribbean-based e-commerce platform, experienced a surge of "carding" attacks—fraudsters testing stolen credit cards through rapid-fire online transactions.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-destructive/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-destructive">The Threat</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-destructive mt-1 flex-shrink-0" />
                      Criminals leveraging unregulated residential proxy networks
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-destructive mt-1 flex-shrink-0" />
                      Each attempt appeared from unique customer in different country
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-destructive mt-1 flex-shrink-0" />
                      Existing IP-based filters became useless
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-destructive/5 border-destructive/20">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold mb-6 text-center">The Stakes</h3>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-destructive mb-2">4,000+</div>
                    <div className="text-sm text-muted-foreground">Fraudulent attempts in one week</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-destructive mb-2">$120,000</div>
                    <div className="text-sm text-muted-foreground">Potential chargebacks</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-destructive mb-2">Critical</div>
                    <div className="text-sm text-muted-foreground">Threat to customer trust</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator className="my-12" />

          {/* Nexx Intervention */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-primary" />
              Nexx Telecom's Ethical Proxy Intervention
            </h2>
            
            <div className="space-y-8">
              {/* Step 1 */}
              <Card className="border-primary/20">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl">1</div>
                    <h3 className="text-2xl font-semibold">Ethical Residential Proxies for Traffic Simulation</h3>
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    We provisioned RetailShield with a controlled residential proxy pool, built on ethical sourcing. Unlike criminal networks—where proxies come from infected or hijacked devices—our residential proxies are consenting, transparent, and legally sourced.
                  </p>
                  <div className="bg-primary/5 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3 text-primary">Impact:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Fraud team could simulate real customer activity across multiple regions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Exposed anomalies in supposedly legitimate traffic patterns</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Step 2 */}
              <Card className="border-secondary/20">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-secondary-foreground font-bold text-xl">2</div>
                    <h3 className="text-2xl font-semibold">Datacenter Proxies for Forensic Analysis</h3>
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    We added high-performance datacenter proxies from Nexx Telecom's secure facilities. These proxies enabled comprehensive forensic analysis and traffic mirroring.
                  </p>
                  <div className="bg-secondary/5 p-4 rounded-lg mb-6">
                    <h4 className="font-semibold mb-3 text-secondary">Capabilities:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-secondary mt-1 flex-shrink-0" />
                        <span>Mirroring suspicious traffic to controlled environments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-secondary mt-1 flex-shrink-0" />
                        <span>Identified 73% of fraud from just two autonomous systems</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-secondary mt-1 flex-shrink-0" />
                        <span>Fed enriched intelligence into fraud prevention engine</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-accent p-4 rounded-lg">
                    <p className="font-medium text-accent-foreground">
                      <strong>Result:</strong> The fraud was unmasked as a coordinated botnet operation, not random customer behavior.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Step 3 */}
              <Card className="border-accent/20">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-bold text-xl">3</div>
                    <h3 className="text-2xl font-semibold">Compliance-Driven Data Sharing</h3>
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Because Nexx Telecom enforces auditable proxy logs and strict compliance policies, the intelligence gathered was immediately useful to both RetailShield and regulators.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-accent/5 rounded-lg">
                      <Lock className="h-8 w-8 text-accent mx-auto mb-2" />
                      <div className="font-medium">Blocked</div>
                      <div className="text-sm text-muted-foreground">Fraudulent IP clusters at gateway</div>
                    </div>
                    <div className="text-center p-4 bg-accent/5 rounded-lg">
                      <Shield className="h-8 w-8 text-accent mx-auto mb-2" />
                      <div className="font-medium">Shared</div>
                      <div className="text-sm text-muted-foreground">Intelligence with law enforcement</div>
                    </div>
                    <div className="text-center p-4 bg-accent/5 rounded-lg">
                      <CheckCircle className="h-8 w-8 text-accent mx-auto mb-2" />
                      <div className="font-medium">Prevented</div>
                      <div className="text-sm text-muted-foreground">Massive chargebacks</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator className="my-12" />

          {/* Comparison Table */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Why Ethical Proxy Use is Different</h2>
            
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-6 font-semibold">Aspect</th>
                        <th className="text-left p-6 font-semibold text-destructive">Unethical Proxy Use</th>
                        <th className="text-left p-6 font-semibold text-primary">Ethical Proxy Use</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-6 font-medium">Source of IPs</td>
                        <td className="p-6 text-muted-foreground">Hijacked devices, malware, non-consenting users</td>
                        <td className="p-6 text-muted-foreground">Consenting partners, legitimate leases</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-6 font-medium">Purpose</td>
                        <td className="p-6 text-muted-foreground">Mask identity, commit fraud, exploit vulnerabilities</td>
                        <td className="p-6 text-muted-foreground">Detect fraud, monitor anomalies, compliance testing</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-6 font-medium">Transparency</td>
                        <td className="p-6 text-muted-foreground">Hidden, obfuscated, no accountability</td>
                        <td className="p-6 text-muted-foreground">Full audit logs, chain of custody, compliance-driven</td>
                      </tr>
                      <tr>
                        <td className="p-6 font-medium">Outcome</td>
                        <td className="p-6 text-muted-foreground">Financial loss, reputational damage, legal risk</td>
                        <td className="p-6 text-muted-foreground">Fraud prevention, trust-building, regulatory strength</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator className="my-12" />

          {/* Conclusion */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Conclusion</h2>
            
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-xl">
              <p className="text-lg leading-relaxed mb-6">
                Proxies are not inherently malicious—they are neutral tools. In the hands of criminals, they obscure fraud. In the hands of ethical providers like Nexx Telecom, they expose fraud.
              </p>
              <p className="text-lg leading-relaxed">
                The RetailShield case proves that with ethical sourcing, compliance enforcement, and forensic intelligence, proxies can transform from instruments of deception into weapons of defense against internet fraud.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Ready to Implement Ethical Proxy Solutions?</h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Partner with Nexx Telecom to deploy compliance-driven proxy infrastructure that protects your business while maintaining the highest ethical standards.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="https://nexx.net/contact">
                    <Button size="lg" className="px-8">
                      Contact Our Team
                    </Button>
                  </Link>
                  <Link to="https://nexx.net/residential-proxies">
                    <Button variant="outline" size="lg" className="px-8">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        </article>

        {/* Footer */}
        <footer className="border-t bg-muted/30">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <img 
                  src="/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png" 
                  alt="NEXX"
                  className="h-6 w-auto"
                />
                <span className="text-sm text-muted-foreground">© 2024 NEXX Telecom. All rights reserved.</span>
              </div>
              <div className="flex items-center space-x-4">
                <Link to="https://nexx.net" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Main Site
                </Link>
                <Link to="https://nexx.net/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Privacy
                </Link>
                <Link to="https://nexx.net/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Terms
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default EthicalProxyArticle;